import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { useSiteContent } from "@/lib/useSiteContent";

function ArticleCard({ a }) {
  return (
    <Link
      to={`/insights/${a.slug}`}
      data-testid={`hub-card-${a.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-[#102036]/10 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_45px_rgb(16,32,54,0.09)]"
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={a.image}
          alt={a.title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
          loading="lazy"
        />
        <span className="absolute left-4 top-4 rounded-full bg-[#102036]/85 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur">
          {a.category}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        {a.date_display && (
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#3682AE]">{a.date_display}</p>
        )}
        <h3 className="mt-2 font-display text-xl font-semibold leading-snug text-[#102036]">{a.title}</h3>
        <p className="mt-2 flex-1 text-[13px] leading-relaxed text-[#4A5568]">{a.excerpt}</p>
        <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#3682AE]">
          Read article <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export default function InsightsHub() {
  const { articles } = useSiteContent();
  const all = articles || [];
  const series = all.filter((a) => a.category === "Money Myths & Tax Facts");
  const desk = all.filter((a) => a.category !== "Money Myths & Tax Facts");

  useEffect(() => {
    document.title = "Tax Insights — All Articles | Ford & Associates";
    return () => { document.title = "Ford & Associates | Accounting, Tax & Advisory — Newmarket, Ontario"; };
  }, []);

  return (
    <div className="bg-[#FFFFFF] pb-24" data-testid="insights-hub-page">
      <section className="relative overflow-hidden bg-[#102036] pt-[72px]">
        <div className="grain pointer-events-none absolute inset-0" aria-hidden="true" />
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#3682AE]/25 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1320px] px-5 py-16 sm:px-8 lg:py-20">
          <Eyebrow light>Tax Insights</Eyebrow>
          <h1 className="mt-4 font-display text-4xl font-medium tracking-tight text-white sm:text-5xl">
            Every article from our tax desk — all in one place.
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-white/70">
            Plain-language notes on deadlines, deductions, bookkeeping and the tax myths we hear most —
            including our complete <em className="font-display">Money Myths &amp; Tax Facts</em> weekly series.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1320px] px-5 py-16 sm:px-8" data-testid="hub-desk-articles">
        <Reveal>
          <Eyebrow>Latest from our tax desk</Eyebrow>
        </Reveal>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {desk.map((a, i) => (
            <Reveal key={a.slug} delay={0.05 * (i % 3)}>
              <ArticleCard a={a} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1320px] px-5 pb-4 sm:px-8" data-testid="hub-series-articles">
        <Reveal>
          <div className="flex flex-col gap-2 border-t border-[#102036]/10 pt-12 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <Eyebrow>The complete series</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-[#102036] sm:text-4xl">
                Money Myths &amp; Tax Facts.
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#4A5568]">
                Our weekly myth-busting series — one tax myth at a time, what the CRA actually allows, and the
                documentation that keeps you safe.
              </p>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#4A5568]/60">{series.length} posts</p>
          </div>
        </Reveal>
        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {series.map((a, i) => (
            <Reveal key={a.slug} delay={0.05 * (i % 3)}>
              <ArticleCard a={a} />
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
